import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";
import CircularProgress from "@mui/material/CircularProgress";
import { Download, Search, Calendar, BellOff, Bell } from "lucide-react";
import jsPDF from "jspdf";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

function Announcements() {
  const [searchTerm, setSearchTerm] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-out-cubic", once: true });

    const fetchAnnouncements = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/api/announcements`);
        if (!response.ok) throw new Error("Failed to fetch announcements");
        const data = await response.json();
        // Sort by newest first
        const sortedData = data.sort(
          (a, b) => new Date(b.date) - new Date(a.date),
        );
        setAnnouncements(sortedData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const filteredAnnouncements = announcements.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDownloadAllPDF = () => {
    const pdf = new jsPDF("p", "mm", "a4");
    let y = 60;
    const pageHeight = pdf.internal.pageSize.height;

    // === Logo ===
    const logoUrl = "/eusda-logo.png";
    try {
      pdf.addImage(logoUrl, "PNG", 90, 10, 30, 30);
    } catch (e) {
      console.warn("Logo not found for PDF");
    }

    // === Title ===
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(22, 101, 52); // Green color
    pdf.text("EUSDA Church Announcements", 105, 50, { align: "center" });
    pdf.setDrawColor(22, 163, 74);
    pdf.setLineWidth(0.5);
    pdf.line(40, 53, 170, 53);

    // === Announcements ===
    filteredAnnouncements.forEach((a, i) => {
      if (y > pageHeight - 50) {
        pdf.addPage();
        y = 20;
      }

      // Title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${i + 1}. ${a.title}`, 14, y);
      y += 6;

      // Date
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      const dateStr = new Date(a.date).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
      pdf.text(dateStr, 14, y);
      y += 6;

      // Description
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.setTextColor(50);
      const splitDesc = pdf.splitTextToSize(a.description, 180);
      pdf.text(splitDesc, 14, y);
      y += splitDesc.length * 5 + 8;

      // Separator
      pdf.setDrawColor(230);
      pdf.line(14, y - 4, 196, y - 4);
    });

    // === Footer ===
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(9);
      pdf.setTextColor(150);
      pdf.text(
        `Generated by EUSDA Church Portal — ${new Date().toLocaleDateString()}`,
        105,
        pageHeight - 10,
        { align: "center" },
      );
      pdf.text(`Page ${i} of ${totalPages}`, 190, pageHeight - 10, {
        align: "right",
      });
    }

    pdf.save("EUSDA_Announcements.pdf");
  };

  return (
    <section className="bg-gray-50 min-h-screen pt-24 pb-20 px-4 sm:px-6 mt-[-8rem] md:mt-[-4rem]">
      <Helmet>
        <title>Announcements | EUSDA Church Kenya</title>
        <meta
          name="description"
          content="Stay updated with the latest announcements from Egerton University SDA Church. Download official notices and keep track of upcoming events and activities."
        />
        <meta
          name="keywords"
          content="EUSDA announcements, SDA Kenya news, church updates, Egerton University Seventh-day Adventist announcements, upcoming events"
        />
        <link rel="canonical" href="https://eusda.co.ke/announcements" />

        {/* Open Graph */}
        <meta property="og:title" content="Church Announcements | EUSDA" />
        <meta
          property="og:description"
          content="Stay informed with the latest announcements and updates from Egerton University SDA Church."
        />
        <meta property="og:url" content="https://eusda.co.ke/announcements" />
        <meta
          property="og:image"
          content="https://eusda.co.ke/eusda-logo.png"
        />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Church Announcements | EUSDA" />
        <meta
          name="twitter:description"
          content="Latest announcements and updates from EUSDA Church Kenya."
        />
        <meta
          name="twitter:image"
          content="https://eusda.co.ke/eusda-logo.png"
        />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: "Church Announcements - EUSDA",
            description:
              "Official announcements and updates from Egerton University SDA Church.",
            url: "https://eusda.co.ke/announcements",
            publisher: {
              "@type": "Organization",
              name: "EUSDA Church",
              url: "https://eusda.co.ke",
            },
          })}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12" data-aos="fade-down">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Bell size={16} className="fill-green-800" /> Latest Updates
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            Church Board <span className="text-green-700">Announcements</span>
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Stay informed about upcoming events, schedule changes, and important
            notices from the church leadership.
          </p>
        </div>

        {/* Controls Bar */}
        <div
          className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-10 flex flex-col md:flex-row items-center justify-between gap-4"
          data-aos="fade-up"
        >
          {/* Search Input */}
          <div className="relative w-full md:w-96">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by title or keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition-all"
            />
          </div>

          {/* Download Button */}
          {filteredAnnouncements.length > 0 && (
            <button
              onClick={handleDownloadAllPDF}
              className="w-full md:w-auto flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white px-6 py-2.5 rounded-xl font-medium shadow-md transition-transform active:scale-95"
            >
              <Download size={18} />
              Download PDF
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <CircularProgress color="success" size={50} thickness={4} />
            <p className="text-gray-500 mt-4 animate-pulse">
              Fetching updates...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-red-700 mb-8 max-w-2xl mx-auto">
            <p className="font-bold">Error loading announcements</p>
            <p>{error}</p>
          </div>
        )}

        {/* Grid Layout */}
        {!loading && !error && (
          <>
            {filteredAnnouncements.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredAnnouncements.map((announcement, index) => (
                  <div
                    key={announcement._id || index}
                    className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
                    data-aos="fade-up"
                    data-aos-delay={index * 50}
                  >
                    {/* Header: Date */}
                    <div className="flex items-center gap-2 text-sm text-green-600 font-semibold mb-3 bg-green-50 w-fit px-3 py-1 rounded-lg">
                      <Calendar size={14} />
                      {new Date(announcement.date).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-700 transition-colors">
                      {announcement.title}
                    </h2>

                    {/* Divider */}
                    <div className="h-px bg-gray-100 w-full mb-3"></div>

                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line flex-grow">
                      {announcement.description}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State */
              <div
                className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-gray-200"
                data-aos="zoom-in"
              >
                <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BellOff size={32} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  No announcements found
                </h3>
                <p className="text-gray-500">
                  {searchTerm
                    ? "Try adjusting your search terms."
                    : "Check back later for new updates."}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default Announcements;
